import { useState, useEffect, useCallback } from 'react'
import { leadProvidersApi, type LeadProvider } from '../api/leadProviders'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  useSnackbar,
  useConfirm,
  FormSection,
} from '../components/ui'

/**
 * TASK-0157 (REQ-105-001) — anagrafica Fornitori Lead. Same list+modal-form shape as
 * `DepartmentsPage.tsx` (Catalog module): a table of active providers, a create/edit modal with
 * every field, edit/deactivate row actions. Purely a CRUD anagrafica — no relation to
 * Lead/Campaign is built here (out of scope for this task, see `LeadProvider.cs`'s doc comment).
 */
interface LeadProviderFormState {
  name: string
  contactInfo: string
  contractReference: string
  cost: string
  billingMode: string
  sectors: string
  sla: string
  integrationMethod: string
  notes: string
}

const EMPTY_FORM: LeadProviderFormState = {
  name: '',
  contactInfo: '',
  contractReference: '',
  cost: '',
  billingMode: '',
  sectors: '',
  sla: '',
  integrationMethod: '',
  notes: '',
}

export function LeadProvidersPage() {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const [items, setItems] = useState<LeadProvider[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<LeadProviderFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await leadProvidersApi.list()
      setItems(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  function openCreate() {
    setForm(EMPTY_FORM)
    setEditId(null)
    setSaveError('')
    setShowModal(true)
  }

  function openEdit(p: LeadProvider) {
    setForm({
      name: p.name,
      contactInfo: p.contactInfo ?? '',
      contractReference: p.contractReference ?? '',
      cost: p.cost != null ? String(p.cost) : '',
      billingMode: p.billingMode ?? '',
      sectors: p.sectors ?? '',
      sla: p.sla ?? '',
      integrationMethod: p.integrationMethod ?? '',
      notes: p.notes ?? '',
    })
    setEditId(p.id)
    setSaveError('')
    setShowModal(true)
  }

  function closeModal() {
    setShowModal(false)
    setEditId(null)
    setSaveError('')
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setSaveError('Il nome è obbligatorio')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      const payload = {
        name: form.name,
        contactInfo: form.contactInfo || undefined,
        contractReference: form.contractReference || undefined,
        cost: form.cost !== '' ? parseFloat(form.cost) : undefined,
        billingMode: form.billingMode || undefined,
        sectors: form.sectors || undefined,
        sla: form.sla || undefined,
        integrationMethod: form.integrationMethod || undefined,
        notes: form.notes || undefined,
      }
      if (editId !== null) {
        await leadProvidersApi.update(editId, payload)
      } else {
        await leadProvidersApi.create(payload)
      }
      closeModal()
      void load()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(p: LeadProvider) {
    if (!(await askConfirm({ title: 'Disattiva fornitore', message: `Disattivare il fornitore "${p.name}"?`, confirmLabel: 'Disattiva', destructive: true }))) return
    try {
      await leadProvidersApi.delete(p.id)
      void load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore durante la disattivazione', tone: 'error' })
    }
  }

  return (
    <Page>
      <PageHeader
        title="Fornitori lead"
        icon="storefront"
        count={items.length > 0 ? items.length : undefined}
        actions={
          <Button variant="filled" icon="add" onClick={openCreate}>
            Nuovo fornitore
          </Button>
        }
      />

      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="storefront" title="Nessun fornitore" />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>#</th>
                <th>Nome</th>
                <th>Contatto</th>
                <th>Modalità Fatturazione</th>
                <th>Costo</th>
                <th>Stato</th>
                <th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id}>
                  <td className="text-muted md-num">{p.id}</td>
                  <td className="md-typescale-emphasized-body-medium">{p.name}</td>
                  <td className="text-muted">{p.contactInfo || '—'}</td>
                  <td className="text-muted">{p.billingMode || '—'}</td>
                  <td className="text-muted md-num">{p.cost != null ? `€ ${p.cost.toFixed(2)}` : '—'}</td>
                  <td>
                    {p.isActive ? (
                      <StatusPill tone="success">Attivo</StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Inattivo</StatusPill>
                    )}
                  </td>
                  <td className="text-end">
                    <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(p)} />{' '}
                    <IconButton icon="delete" size="sm" aria-label="Disattiva" onClick={() => void handleDelete(p)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica fornitore' : 'Nuovo fornitore'}
        actions={
          <>
            <Button variant="text" onClick={closeModal}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {saveError && <Callout tone="error">{saveError}</Callout>}
          <FormSection title="Fornitore">
            <TextField
              className="md-span-full"
              label="Nome *"
              type="text"
              placeholder="Nome fornitore"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label="Contatto"
              type="text"
              supportingText="Referente, email, telefono..."
              value={form.contactInfo}
              onChange={e => setForm(f => ({ ...f, contactInfo: e.target.value }))}
            />
            <TextField
              label="Riferimento Contratto"
              type="text"
              supportingText="es. numero contratto"
              value={form.contractReference}
              onChange={e => setForm(f => ({ ...f, contractReference: e.target.value }))}
            />
          </FormSection>
          <FormSection title="Condizioni">
            <TextField
              id="lp-cost"
              label="Costo €"
              type="number"
              min="0"
              step="0.01"
              value={form.cost}
              onChange={e => setForm(f => ({ ...f, cost: e.target.value }))}
            />
            <TextField
              label="Modalità di Fatturazione"
              type="text"
              supportingText="es. a lead, fisso mensile..."
              value={form.billingMode}
              onChange={e => setForm(f => ({ ...f, billingMode: e.target.value }))}
            />
            <TextField
              label="Settori"
              type="text"
              supportingText="Elenco settori"
              value={form.sectors}
              onChange={e => setForm(f => ({ ...f, sectors: e.target.value }))}
            />
            <TextField
              label="SLA"
              type="text"
              supportingText="Termini SLA"
              value={form.sla}
              onChange={e => setForm(f => ({ ...f, sla: e.target.value }))}
            />
          </FormSection>
          <FormSection title="Integrazione">
            <TextField
              className="md-span-full"
              label="Metodo di Integrazione"
              type="text"
              supportingText="es. API, CSV, Manuale..."
              value={form.integrationMethod}
              onChange={e => setForm(f => ({ ...f, integrationMethod: e.target.value }))}
            />
            <TextField
              className="md-span-full"
              label="Note"
              multiline
              rows={3}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </FormSection>
        </>
      </Dialog>
    </Page>
  )
}
